import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import EmployeeForm from "@/pages/EmployeeForm";

const EMPTY_COL = { biometric: "", userid: "", employee: "", company: "", department: "", classification: "", supervisor: "", status: "" };

const STATUS_TONE = {
  "Active": "bg-emerald-50 text-emerald-700",
  "For Clearance": "bg-amber-50 text-amber-700",
  "Cleared/Resigned": "bg-slate-100 text-slate-600",
  "Inactive": "bg-slate-100 text-slate-500",
  "Terminated": "bg-rose-50 text-rose-700",
};

const CLEARANCE_ACTIONS = [
  ["For Clearance", "Start Clearance"],
  ["Cleared/Resigned", "Mark Cleared / Resigned"],
  ["Inactive", "Set Inactive"],
  ["Terminated", "Terminate"],
  ["Active", "Reinstate (Active)"],
];

function StatCard({ label, value, tone, testid }) {
  const tones = {
    blue: "bg-blue-500 text-white",
    red: "bg-rose-400 text-white",
    white: "bg-white border border-slate-200 text-emerald-600",
  };
  return (
    <div className={`rounded-xl p-5 ${tones[tone]}`} data-testid={testid}>
      <div className={`text-sm font-medium ${tone === "white" ? "text-slate-500" : "text-white/90"}`}>{label}</div>
      <div className="text-3xl font-bold mt-2">{value ?? 0}</div>
    </div>
  );
}

export default function Hris() {
  const { hasPerm, user: me } = useAuth();
  const canEdit = hasPerm("employee.edit");
  const hc = (k) => !me?.hris_limited || (me?.hris_perms || []).includes(k);
  const [emps, setEmps] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [refs, setRefs] = useState({ roles: [], companies: [], geofences: [], schedule_types: [], managers: [], departments: [] });
  const [stats, setStats] = useState({ classification: [], gender: [], cards: { for_clearance: 0, cleared: 0, active: 0 } });
  const [q, setQ] = useState("");
  const [company, setCompany] = useState("all");
  const [department, setDepartment] = useState("all");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [formMode, setFormMode] = useState(null);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [creds, setCreds] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [assignSched, setAssignSched] = useState("");
  // table controls
  const [tableSearch, setTableSearch] = useState("");
  const [colFilter, setColFilter] = useState(EMPTY_COL);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState({ key: "", dir: 1 });
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (company !== "all") params.set("company_id", company);
    if (department !== "all") params.set("department", department);
    api.get(`/employees?${params.toString()}`).then(({ data }) => setEmps(data)).finally(() => setLoading(false));
    const sp = company !== "all" ? `?company_id=${company}` : "";
    api.get(`/employees/stats${sp}`).then(({ data }) => setStats(data)).catch(() => {});
  }, [q, company, department]);

  useEffect(() => { api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {}); if (canEdit) api.get("/hris/registration-refs").then(({ data }) => setRefs(data)).catch(() => {}); }, [canEdit]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const companyName = useCallback((id) => companies.find((c) => c.id === id)?.name || "—", [companies]);
  const empMap = useMemo(() => {
    const m = {};
    emps.forEach((e) => { m[e.id] = { name: `${e.first_name} ${e.last_name}`, photo: e.photo_url }; });
    return m;
  }, [emps]);
  const supervisorName = useCallback((e) => (e.manager_id && empMap[e.manager_id]?.name) || "—", [empMap]);

  const deptOptions = useMemo(() => {
    const set = new Set();
    (refs.departments || []).forEach((d) => set.add(typeof d === "string" ? d : d.name));
    emps.forEach((e) => e.department && set.add(e.department));
    return [...set].filter(Boolean).sort();
  }, [refs.departments, emps]);

  const rowVals = useCallback((e) => ({
    biometric: e.biometric_id || "",
    userid: e.username || e.employee_no || "",
    employee: `${e.first_name} ${e.last_name}`,
    company: companyName(e.company_id),
    department: e.department || "",
    classification: e.employment_type || "",
    supervisor: supervisorName(e),
    status: e.employment_status || "",
  }), [companyName, supervisorName]);

  const displayed = useMemo(() => {
    let list = emps.map((e) => ({ e, v: rowVals(e) }));
    if (status !== "all") list = list.filter(({ v }) => v.status === status);
    Object.entries(colFilter).forEach(([k, val]) => {
      if (val) list = list.filter(({ v }) => (v[k] || "").toLowerCase().includes(val.toLowerCase()));
    });
    if (tableSearch) {
      const s = tableSearch.toLowerCase();
      list = list.filter(({ v }) => Object.values(v).join(" ").toLowerCase().includes(s));
    }
    if (sort.key) list.sort((a, b) => (a.v[sort.key] || "").localeCompare(b.v[sort.key] || "") * sort.dir);
    return list;
  }, [emps, rowVals, status, colFilter, tableSearch, sort]);

  const total = displayed.length;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const curPage = Math.min(page, pages - 1);
  const slice = displayed.slice(curPage * perPage, curPage * perPage + perPage);
  useEffect(() => { setPage(0); }, [status, colFilter, tableSearch, perPage, emps]);

  const openEdit = async (id) => { const { data } = await api.get(`/employees/${id}`); setEditing(data.employee); setFormMode("edit"); };
  const downloadTemplate = async () => { try { const r = await api.get("/hris/employees/template", { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "employee_template.xlsx"; a.click(); URL.revokeObjectURL(u); } catch { toast.error("Download failed"); } };
  const runBulk = async () => { if (!bulkFile) return; const fd = new FormData(); fd.append("file", bulkFile); try { const { data } = await api.post("/hris/employees/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } }); setBulkResult(data); toast.success(`Imported ${data.inserted}`); if (data.skipped?.length) toast.warning(`${data.skipped.length} duplicate row(s) skipped`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); } };
  const toggleRow = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const applyAssign = async () => { if (!assignSched || selected.length === 0) return; try { const { data } = await api.post(`/hris/schedule-types/${assignSched}/bulk-assign`, { employee_ids: selected }); toast.success(`Assigned to ${data.updated} employee(s)`); setSelected([]); setAssignSched(""); load(); } catch { toast.error("Failed"); } };
  const resetFace = async (id, name) => { if (!window.confirm(`Reset Face ID for ${name}? They will re-register and retake their profile photo on their next clock-in.`)) return; try { await api.post(`/attendance/face-reset/${id}`); toast.success("Face ID reset"); load(); } catch { toast.error("Reset failed"); } };
  const setClearance = async (id, status, name) => { const warn = (status === "Terminated" || status === "Cleared/Resigned") ? " Their login will be disabled." : (status === "Active" ? " Their login will be re-enabled." : ""); if (!window.confirm(`Set ${name}'s status to "${status}"?${warn}`)) return; try { await api.post(`/employees/${id}/clearance`, { status }); toast.success(`Status → ${status}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); } };
  const resetFilters = () => { setQ(""); setCompany("all"); setDepartment("all"); setStatus("all"); setColFilter(EMPTY_COL); setTableSearch(""); };
  const exportCsv = () => {
    const cols = ["Biometric Code", "User ID", "Employee", "Company", "Department", "Classification", "Immediate Supervisor", "Status"];
    const rows = displayed.map(({ v }) => [v.biometric, v.userid, v.employee, v.company, v.department, v.classification, v.supervisor, v.status]);
    const csv = [cols, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "employees.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));
  const from = total === 0 ? 0 : curPage * perPage + 1;
  const to = Math.min(total, curPage * perPage + perPage);

  const HEAD = [
    ["biometric", "Biometric Code"], ["userid", "User ID"], ["employee", "Employee"], ["company", "Company"],
    ["department", "Department"], ["classification", "Classification"], ["supervisor", "Immediate Supervisor"], ["status", "Status"],
  ];

  return (
    <div className="uemp-fade-in">
      <PageHeader title="HRIS — Employee Directory" subtitle="Authoritative directory of all employees" />

      {/* Summary panel */}
      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5" data-testid="emp-summary-classification">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <div className="flex items-center justify-between text-sm font-semibold text-slate-800 border-b border-slate-100 pb-2 mb-1">
                <span>Employee Classification (Active)</span><span>Total</span>
              </div>
              {stats.classification.length === 0 && <div className="text-xs text-slate-400 py-2">No data</div>}
              {stats.classification.map((c) => (
                <div key={c.label} className="flex items-center justify-between py-1.5 border-b border-slate-50 text-sm">
                  <span className="text-slate-600">{c.label}</span>
                  <span className="text-blue-600 font-medium">{c.total.toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="flex items-center justify-between text-sm font-semibold text-slate-800 border-b border-slate-100 pb-2 mb-1">
                <span>Employee Gender (Active)</span><span>Total</span>
              </div>
              {stats.gender.length === 0 && <div className="text-xs text-slate-400 py-2">No data</div>}
              {stats.gender.map((g) => (
                <div key={g.label} className="flex items-center justify-between py-1.5 border-b border-slate-50 text-sm">
                  <span className="text-slate-600">{g.label}</span>
                  <span className="text-blue-600 font-medium">{g.total.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4 content-start">
          <StatCard label="For Clearance" value={stats.cards.for_clearance} tone="blue" testid="emp-card-for-clearance" />
          <StatCard label="Cleared" value={stats.cards.cleared} tone="red" testid="emp-card-cleared" />
          <StatCard label="Active" value={stats.cards.active} tone="white" testid="emp-card-active" />
        </div>
      </div>

      {/* Employees section */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-slate-900">Employees</h2>
          {canEdit && hc("dir.add") && <Button size="icon" variant="outline" className="h-7 w-7 rounded-full text-emerald-600 border-emerald-200" title="Add employee" onClick={() => { setEditing(null); setFormMode("add"); }} data-testid="add-employee-button"><Icons.Plus className="h-4 w-4" /></Button>}
          {canEdit && hc("dir.import") && <Button size="icon" variant="outline" className="h-7 w-7 rounded-full text-amber-500 border-amber-200" title="Bulk import" onClick={() => { setBulkResult(null); setBulkFile(null); setBulkOpen(true); }} data-testid="bulk-import-button"><Icons.Upload className="h-4 w-4" /></Button>}
        </div>
        {hc("dir.export") && <Button variant="outline" size="sm" onClick={exportCsv} data-testid="emp-export-csv"><Icons.Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>}
      </div>

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4" data-testid="emp-filter-bar">
        <div className="text-sm font-semibold text-slate-700 mb-3">Filter</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-center">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Name / Biometric Code" data-testid="emp-filter-search" />
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger data-testid="emp-filter-company"><SelectValue placeholder="Select Company" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Companies</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={department} onValueChange={setDepartment}>
            <SelectTrigger data-testid="emp-filter-department"><SelectValue placeholder="Select Department" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Departments</SelectItem>{deptOptions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="emp-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Active">Active</SelectItem><SelectItem value="For Clearance">For Clearance</SelectItem><SelectItem value="Cleared/Resigned">Cleared/Resigned</SelectItem><SelectItem value="Inactive">Inactive</SelectItem><SelectItem value="Terminated">Terminated</SelectItem></SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button onClick={load} className="bg-indigo-600 hover:bg-indigo-700 flex-1" data-testid="emp-filter-apply">Filter</Button>
            <Button onClick={resetFilters} className="bg-amber-400 hover:bg-amber-500 text-slate-900 flex-1" data-testid="emp-filter-reset">Reset Filter</Button>
          </div>
        </div>
      </div>

      {canEdit && selected.length > 0 && (
        <div className="flex items-center gap-3 bg-slate-900 text-white rounded-xl px-4 py-2.5 mb-3" data-testid="assign-toolbar">
          <span className="text-sm">{selected.length} selected</span>
          <Select value={assignSched} onValueChange={setAssignSched}><SelectTrigger className="w-56 h-8 bg-white/10 border-white/20 text-white" data-testid="assign-schedule-select"><SelectValue placeholder="Assign schedule type…" /></SelectTrigger><SelectContent>{refs.schedule_types.map((s) => <SelectItem key={s.id} value={s.id}>{s.shift_start ? `${s.name} · ${s.shift_start}–${s.shift_end}${s.flexible ? " (Flexible)" : ""}` : s.name}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" onClick={applyAssign} disabled={!assignSched} className="bg-blue-600 hover:bg-blue-700" data-testid="assign-apply">Apply</Button>
          <button onClick={() => setSelected([])} className="ml-auto text-slate-400 hover:text-white text-sm">Clear</button>
        </div>
      )}

      {/* Table controls */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <label className="text-sm text-slate-500 flex items-center gap-2">Show
          <select className="border border-slate-200 rounded-md h-8 px-2 text-sm bg-white" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} data-testid="emp-per-page">
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select> entries
        </label>
        <label className="text-sm text-slate-500 flex items-center gap-2">Search:
          <Input value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} className="h-8 w-48" data-testid="emp-table-search" />
        </label>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 whitespace-nowrap">
              {canEdit && <TableHead className="w-8"></TableHead>}
              {HEAD.map(([key, label]) => (
                <TableHead key={key} className="cursor-pointer select-none" onClick={() => toggleSort(key)}>
                  <span className="inline-flex items-center gap-1">{label}<Icons.ChevronsUpDown className="h-3 w-3 text-slate-400" /></span>
                </TableHead>
              ))}
              {canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
            <TableRow className="bg-white">
              {canEdit && <TableHead className="w-8"></TableHead>}
              {HEAD.map(([key]) => (
                <TableHead key={key} className="py-1.5">
                  <Input value={colFilter[key]} onChange={(e) => setColFilter((f) => ({ ...f, [key]: e.target.value }))} placeholder={HEAD.find((h) => h[0] === key)[1]} className="h-7 text-xs" data-testid={`emp-col-filter-${key}`} />
                </TableHead>
              ))}
              {canEdit && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody data-testid="hris-employee-table">
            {loading && <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-8">Loading…</TableCell></TableRow>}
            {!loading && slice.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-8">No employees found.</TableCell></TableRow>}
            {slice.map(({ e, v }) => (
              <TableRow key={e.id} className="hover:bg-slate-50 whitespace-nowrap" data-testid={`employee-row-${e.employee_no}`}>
                {canEdit && <TableCell><input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggleRow(e.id)} data-testid={`emp-select-${e.employee_no}`} /></TableCell>}
                <TableCell className="font-mono text-xs text-slate-600">{v.biometric || "—"}</TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{v.userid || "—"}</TableCell>
                <TableCell className="cursor-pointer" onClick={() => navigate(`/hris/${e.id}`)}>
                  <div className="flex items-center gap-2.5">
                    {e.photo_url ? <img src={e.photo_url} alt="" className="h-7 w-7 rounded-full object-cover shrink-0" data-testid={`emp-avatar-${e.employee_no}`} /> : <div className="h-7 w-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[10px] font-semibold shrink-0">{e.first_name[0]}{e.last_name[0]}</div>}
                    <span className="font-medium text-slate-800 text-sm">{v.employee}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{v.company}</TableCell>
                <TableCell className="text-sm text-slate-600">{v.department || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">{v.classification || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">
                  {e.manager_id && empMap[e.manager_id] ? (
                    <span className="inline-flex items-center gap-2">
                      {empMap[e.manager_id].photo ? <img src={empMap[e.manager_id].photo} alt="" className="h-6 w-6 rounded-full object-cover" /> : <span className="h-6 w-6 rounded-full bg-slate-100" />}
                      {v.supervisor}
                    </span>
                  ) : "—"}
                </TableCell>
                <TableCell><Badge className={`${STATUS_TONE[v.status] || "bg-slate-100 text-slate-600"} hover:opacity-100 text-[10px]`}>{v.status || "—"}</Badge></TableCell>
                {canEdit && <TableCell className="text-right whitespace-nowrap">{hc("dir.face") && <Button size="icon" variant="ghost" className="h-7 w-7" title="Reset Face ID" onClick={() => resetFace(e.id, v.employee)} data-testid={`emp-face-reset-${e.employee_no}`}><Icons.ScanFace className="h-4 w-4 text-slate-500" /></Button>}{(hc("dir.view") || hc("dir.edit")) && <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit" onClick={() => openEdit(e.id)} data-testid={`emp-edit-${e.employee_no}`}><Icons.Pencil className="h-4 w-4 text-slate-500" /></Button>}{hc("dir.clearance") && <DropdownMenu><DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-7 w-7" title="Clearance / Status" data-testid={`emp-clearance-${e.employee_no}`}><Icons.MoreVertical className="h-4 w-4 text-slate-500" /></Button></DropdownMenuTrigger><DropdownMenuContent align="end"><DropdownMenuLabel className="text-xs">Offboarding / Status</DropdownMenuLabel><DropdownMenuSeparator />{CLEARANCE_ACTIONS.map(([st, label]) => <DropdownMenuItem key={st} disabled={v.status === st} onClick={() => setClearance(e.id, st, v.employee)} data-testid={`emp-clearance-opt-${e.employee_no}`}>{label}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu>}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
        <div className="text-sm text-slate-500" data-testid="emp-showing">Showing {from} to {to} of {total} entries</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={curPage === 0} onClick={() => setPage(curPage - 1)} data-testid="emp-prev">Previous</Button>
          <Button variant="outline" size="sm" disabled={curPage >= pages - 1} onClick={() => setPage(curPage + 1)} data-testid="emp-next">Next</Button>
        </div>
      </div>

      {formMode && <EmployeeForm mode={formMode} initial={editing} refs={refs} onClose={() => setFormMode(null)} onSaved={load} onCreated={(c) => { setCreds(c); load(); }} />}

      <Dialog open={!!creds} onOpenChange={() => setCreds(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Icons.CheckCircle2 className="h-5 w-5 text-emerald-500" /> Employee Created</DialogTitle><DialogDescription>Share these one-time credentials with {creds?.name}. They won't be shown again.</DialogDescription></DialogHeader>
          {creds && <div className="bg-slate-900 text-white rounded-lg p-4 font-mono text-sm space-y-1" data-testid="temp-credentials"><div><span className="text-slate-400">Username: </span>{creds.username}</div><div><span className="text-slate-400">Temp password: </span>{creds.password}</div></div>}
          <DialogFooter><Button onClick={() => { navigator.clipboard?.writeText(`Username: ${creds?.username}\nPassword: ${creds?.password}`); toast.success("Copied"); }} variant="outline">Copy</Button><Button onClick={() => setCreds(null)} className="bg-slate-900 hover:bg-slate-800">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Icons.Upload className="h-5 w-5" /> Bulk Import Employees</DialogTitle><DialogDescription>Download the template (same sections as the Employee form). Required: First Name, Last Name, Work Email, Company. Duplicates (Employee No., email, full name + birth date, SSS / PhilHealth / HDMF / TIN) are skipped and listed below.</DialogDescription></DialogHeader>
          <button onClick={downloadTemplate} className="self-start text-sm text-blue-600 font-medium inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50" data-testid="emp-download-template"><Icons.FileSpreadsheet className="h-4 w-4" /> Download template</button>
          <label className="flex items-center gap-3 border border-slate-200 rounded-lg p-3 cursor-pointer hover:border-slate-400"><span className="bg-slate-100 text-slate-700 text-sm rounded px-3 py-1.5">Choose File</span><span className="text-sm text-slate-500">{bulkFile ? bulkFile.name : "No file chosen"}</span><input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => setBulkFile(e.target.files[0])} data-testid="bulk-file-input" /></label>
          {bulkResult && <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-2 max-h-72 overflow-y-auto" data-testid="bulk-result">
            <div className="flex flex-wrap gap-2">
              <span className="rounded bg-emerald-100 text-emerald-800 px-2 py-0.5 font-medium" data-testid="bulk-inserted-count">Imported: {bulkResult.inserted}</span>
              <span className="rounded bg-amber-100 text-amber-800 px-2 py-0.5 font-medium" data-testid="bulk-skipped-count">Duplicates skipped: {bulkResult.skipped?.length || 0}</span>
              <span className="rounded bg-red-100 text-red-700 px-2 py-0.5 font-medium" data-testid="bulk-error-count">Errors: {bulkResult.errors?.length || 0}</span>
            </div>
            {bulkResult.skipped?.length > 0 && <div data-testid="bulk-skipped-list"><div className="font-medium text-amber-800">Skipped (duplicates)</div>{bulkResult.skipped.map((s, i) => <div key={i} className="text-amber-700">Row {s.row} · {s.name}: {s.reason}</div>)}</div>}
            {bulkResult.errors?.length > 0 && <div data-testid="bulk-error-list"><div className="font-medium text-red-700">Not imported (fix and re-upload)</div>{bulkResult.errors.map((s, i) => <div key={i} className="text-red-600">Row {s.row} · {s.name}: {s.reason}</div>)}</div>}
            {Object.keys(bulkResult.temp_passwords || {}).length > 0 && <div className="pt-1 border-t border-slate-200"><div className="font-medium text-slate-700">Temp passwords</div>{Object.entries(bulkResult.temp_passwords).map(([em, pw]) => <div key={em} className="font-mono">{em}: {pw}</div>)}</div>}
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setBulkOpen(false)}>Close</Button><Button onClick={runBulk} disabled={!bulkFile} className="bg-slate-900 hover:bg-slate-800" data-testid="bulk-upload-submit"><Icons.Upload className="h-4 w-4 mr-1.5" /> Upload</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
